import {
  Options,
  useRequestFormStore,
} from 'src/features/requests/request-form/use-request-form-store';
import { useConfirm } from 'src/components/ConfirmModal';
import { useShallow } from 'zustand/react/shallow';
import { LocalStorage } from 'src/services/LocalStorage';
import { useVideoVolumeModalStore } from 'src/features/company/use-video-volume-modal-store';
import { useBrandSelection } from 'src/providers/BrandSelectionProvider/brand-selection-context';
import * as brandClient from 'src/lib/services/api/brand-api/brand-client';
import { BrandResponse } from 'src/lib/services/api/brand-api/brand-client.types';
import { requestClient } from 'src/lib/services/api/request-api';

const useOpenRequestModal = () => {
  const { selectedBrand, selectedBrandHasVideoCount } = useBrandSelection();
  const { setIsModalOpen: setVideoVolumeModalOpen } = useVideoVolumeModalStore(
    useShallow(({ setIsModalOpen }) => ({ setIsModalOpen })),
  );

  const { isMinimized, setCurrentRequestId, openModal, reset } = useRequestFormStore(
    useShallow(({ isMinimized, setCurrentRequestId, openModal, reset }) => ({
      isMinimized,
      setCurrentRequestId,
      openModal,
      reset,
    })),
  );
  const { confirm } = useConfirm();
  const openRequestModal = async (
    requestId?: string,
    options?: Options,
    onAfterTrialCheck?: () => boolean | void,
  ) => {
    // Gate with Video Volume modal regardless of subscription status
    // Use global state from Brand Selection Provider; fallback to API check when unknown
    if (selectedBrand) {
      // If we know the brand doesn't have video count, check if they have existing requests
      if (selectedBrandHasVideoCount === false) {
        try {
          // Check if brand has any existing requests
          const requestCounts = await requestClient.counts({
            filters: [{ id: 'brand_id', value: selectedBrand.id }],
          });

          // Calculate total requests across all statuses
          const hasExistingRequests = Object.values(requestCounts).some(
            (statusCounts) => statusCounts.total.length > 0,
          );

          // If brand has existing requests, bypass the video volume modal
          if (hasExistingRequests) {
            // Continue to Create Request Modal
          } else {
            // No existing requests, show Video Volume Modal
            setVideoVolumeModalOpen(true);
            return;
          }
        } catch (error) {
          // If API call fails, fall back to showing the modal (safer default)
          setVideoVolumeModalOpen(true);
          return;
        }
      }

      // If unknown state (null), fallback to API call for safety
      if (selectedBrandHasVideoCount === null) {
        try {
          const fullBrandData: BrandResponse = await brandClient.get(selectedBrand.id);

          if (!fullBrandData.brand_metadata?.monthly_video_count) {
            // Check if brand has any existing requests
            const requestCounts = await requestClient.counts({
              filters: [{ id: 'brand_id', value: selectedBrand.id }],
            });

            const hasExistingRequests = Object.values(requestCounts).some(
              (statusCounts) => statusCounts.total.length > 0,
            );

            // Only show modal if no existing requests
            if (!hasExistingRequests) {
              setVideoVolumeModalOpen(true);
              return;
            }
          }
        } catch (error) {
          // do nothing for now
        }
      }

      // If selectedBrandHasVideoCount === true, continue to Create Request Modal
    }

    if (onAfterTrialCheck) {
      const shouldStop = onAfterTrialCheck();
      if (shouldStop) {
        return; // Stop processing if the callback returns true
      }
    }

    if (!isMinimized) {
      LocalStorage.set('formValues', null);
      setCurrentRequestId(requestId);
      // Always reset to step 0 for new requests (media selection)
      const modalOptions = {
        ...options,
        currentStep: 0,
      };
      openModal(modalOptions);
      return;
    }

    const confirmed = await confirm('', {
      modal_header: {
        content: 'Draft request in progress',
      },
      modal_body: {
        content:
          'Looks like you already have a request form open. Opening a new one will discard your draft and any changes you’ve made won’t be saved.',
      },
      confirm: {
        text: 'Discard draft',
      },
      cancel: {
        text: 'Keep editing',
      },
    });

    // modal closed - cancel action and do nothing
    if (confirmed === null) {
      return;
    }

    if (confirmed) {
      reset();
      LocalStorage.set('formValues', null);
      setCurrentRequestId(requestId);
      // Always reset to step 0 for new requests (media selection)
      const modalOptions = {
        ...options,
        currentStep: 0,
      };
      openModal(modalOptions);
      return;
    }

    openModal();
  };

  return { openRequestModal };
};

export { useOpenRequestModal };
